import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useCourse } from '../hooks/useCourses'
import { useEnrollments } from '../hooks/useEnrollments'
import { useProgress } from '../hooks/useProgress'
import { useLessonNotes } from '../hooks/useLessonNotes'
import { useLessonBookmarks } from '../hooks/useLessonBookmarks'
import { useQuizzes } from '../hooks/useQuizzes'
import { useAuth } from '../contexts/AuthContext'
import { useAnalytics } from '../hooks/useAnalytics'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import VideoPlayer from '../components/course-player/VideoPlayer'
import LessonContent from '../components/course-player/LessonContent'
import LessonNotes from '../components/course-player/LessonNotes'
import LessonBookmarks from '../components/course-player/LessonBookmarks'
import LessonQuiz from '../components/course-player/LessonQuiz'
import CourseSidebar from '../components/course-player/CourseSidebar'
import CourseNavigation from '../components/course-player/CourseNavigation'
import SafeIcon from '../common/SafeIcon'
import toast from 'react-hot-toast'
import * as FiIcons from 'react-icons/fi'

const { FiMenu, FiX, FiCheck, FiBook, FiArrowLeft } = FiIcons

const CoursePlayer = () => {
  const { courseSlug, lessonId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { trackEvent } = useAnalytics()

  // Data hooks
  const { course, modules, loading: courseLoading } = useCourse(courseSlug)
  const { isEnrolled } = useEnrollments()
  const { progress, markLessonComplete, isLessonComplete, getCourseProgress } = useProgress(course?.id)
  const { notes, saveNote, loading: notesLoading } = useLessonNotes()
  const { bookmarks, addBookmark, deleteBookmark } = useLessonBookmarks()
  const { quizzes, submitQuizAttempt, getQuizAttempts } = useQuizzes()

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentLesson, setCurrentLesson] = useState(null)
  const [currentModule, setCurrentModule] = useState(null)
  const [activeTab, setActiveTab] = useState('content')
  const [completingLesson, setCompletingLesson] = useState(false)

  // Refs for keyboard shortcuts
  const playerRef = useRef(null)

  // Check enrollment and redirect if needed
  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/course/${courseSlug}` } } })
      return
    }

    if (course && !isEnrolled(course.id)) {
      toast.error('You need to enroll in this course first')
      navigate(`/courses/${courseSlug}`)
      return
    }
  }, [user, course, isEnrolled, navigate, courseSlug])

  // Find current lesson and module
  useEffect(() => {
    if (!modules.length) return

    let targetLesson = null
    let targetModule = null

    if (lessonId) {
      // Find specific lesson
      for (const module of modules) {
        const lesson = module.lessons?.find(l => l.id === lessonId)
        if (lesson) {
          targetLesson = lesson
          targetModule = module
          break
        }
      }
    } else {
      // Find first incomplete lesson
      for (const module of modules) {
        for (const lesson of module.lessons || []) {
          if (!isLessonComplete(lesson.id)) {
            targetLesson = lesson
            targetModule = module
            break
          }
        }
        if (targetLesson) break
      }

      // If all lessons complete, show first lesson
      if (!targetLesson && modules[0]?.lessons?.[0]) {
        targetLesson = modules[0].lessons[0]
        targetModule = modules[0]
      }
    }

    if (targetLesson && targetLesson.id !== currentLesson?.id) {
      setCurrentLesson(targetLesson)
      setCurrentModule(targetModule)

      // Update URL if needed
      if (!lessonId || lessonId !== targetLesson.id) {
        navigate(`/course/${courseSlug}/lesson/${targetLesson.id}`, { replace: true })
      }

      // Track lesson view
      trackEvent('lesson_viewed', {
        courseId: course?.id,
        moduleId: targetModule?.id,
        lessonId: targetLesson.id
      })
    }
  }, [modules, lessonId, isLessonComplete, currentLesson?.id, navigate, courseSlug, course?.id, trackEvent])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      switch (e.key.toLowerCase()) {
        case 'n':
          handleNextLesson()
          break
        case 'p':
          handlePreviousLesson()
          break
        case 'm':
          if (currentLesson && !isLessonComplete(currentLesson.id)) {
            handleMarkComplete()
          }
          break
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [currentLesson])

  const getAllLessons = useCallback(() => {
    return modules.reduce((acc, module) => {
      return acc.concat(module.lessons || [])
    }, [])
  }, [modules])

  const getCurrentLessonIndex = useCallback(() => {
    const allLessons = getAllLessons()
    return allLessons.findIndex(lesson => lesson.id === currentLesson?.id)
  }, [getAllLessons, currentLesson?.id])

  const handleNextLesson = useCallback(() => {
    const allLessons = getAllLessons()
    const currentIndex = getCurrentLessonIndex()
    if (currentIndex < allLessons.length - 1) {
      const nextLesson = allLessons[currentIndex + 1]
      navigate(`/course/${courseSlug}/lesson/${nextLesson.id}`)
    }
  }, [getAllLessons, getCurrentLessonIndex, navigate, courseSlug])

  const handlePreviousLesson = useCallback(() => {
    const allLessons = getAllLessons()
    const currentIndex = getCurrentLessonIndex()
    if (currentIndex > 0) {
      const prevLesson = allLessons[currentIndex - 1]
      navigate(`/course/${courseSlug}/lesson/${prevLesson.id}`)
    }
  }, [getAllLessons, getCurrentLessonIndex, navigate, courseSlug])

  const handleMarkComplete = async () => {
    if (!currentLesson || completingLesson) return

    setCompletingLesson(true)
    try {
      await markLessonComplete(currentLesson.id)
      
      // Track completion
      trackEvent('lesson_completed', {
        courseId: course?.id,
        moduleId: currentModule?.id,
        lessonId: currentLesson.id
      })

      // Auto-advance to next lesson (configurable)
      const shouldAutoAdvance = true // You can make this a user preference
      if (shouldAutoAdvance) {
        setTimeout(() => {
          handleNextLesson()
        }, 1000)
      }
    } catch (error) {
      console.error('Error marking lesson complete:', error)
    } finally {
      setCompletingLesson(false)
    }
  }

  const handleLessonSelect = (lesson, module) => {
    navigate(`/course/${courseSlug}/lesson/${lesson.id}`)
  }

  const handleAddBookmark = (timestamp, note = '') => {
    if (currentLesson) {
      addBookmark(currentLesson.id, timestamp, note)
      trackEvent('bookmark_added', {
        courseId: course?.id,
        moduleId: currentModule?.id,
        lessonId: currentLesson.id,
        timestamp
      })
    }
  }

  if (courseLoading || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  if (!currentLesson) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No lessons available</h1>
          <Link to={`/courses/${courseSlug}`} className="text-nutrition-600 hover:text-nutrition-700">
            Back to course details
          </Link>
        </div>
      </div>
    )
  }

  const courseProgressData = getCourseProgress(modules)
  const currentLessonQuiz = quizzes.find(quiz => quiz.lesson_id === currentLesson.id)
  const quizAttempts = currentLessonQuiz ? getQuizAttempts(currentLessonQuiz.id) : []
  const hasCompletedQuiz = quizAttempts.length > 0
  const canMarkComplete = !currentLessonQuiz?.is_required || hasCompletedQuiz

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <CourseSidebar
        course={course}
        modules={modules}
        currentLesson={currentLesson}
        currentModule={currentModule}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLessonSelect={handleLessonSelect}
        progress={progress}
        isLessonComplete={isLessonComplete}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              <SafeIcon icon={FiMenu} className="w-5 h-5" />
            </button>

            <Link
              to={`/courses/${courseSlug}`}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Course</span>
            </Link>

            <div className="hidden sm:block">
              <nav className="flex items-center space-x-2 text-sm text-gray-500">
                <span>{currentModule?.title}</span>
                <span>/</span>
                <span className="text-gray-900">{currentLesson?.title}</span>
              </nav>
            </div>
          </div>

          {/* Progress Pill */}
          <div className="flex items-center space-x-3">
            <div className="bg-nutrition-100 rounded-full px-3 py-1 flex items-center space-x-2">
              <SafeIcon icon={FiBook} className="w-4 h-4 text-nutrition-600" />
              <span className="text-sm font-medium text-nutrition-800">
                {courseProgressData.percentage}% Complete
              </span>
            </div>
          </div>
        </header>

        {/* Mobile Progress Bar */}
        <div className="lg:hidden bg-white border-b border-gray-200">
          <div className="px-4 py-2">
            <div className="flex justify-between items-center text-xs text-gray-600 mb-1">
              <span>{courseProgressData.completed} of {courseProgressData.total} lessons</span>
              <span>{courseProgressData.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-nutrition-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${courseProgressData.percentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full flex flex-col">
            {/* Lesson Title */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <h1 className="text-2xl font-bold text-gray-900">{currentLesson.title}</h1>
              {currentLesson.estimated_minutes > 0 && (
                <p className="text-gray-600 mt-1">
                  Estimated time: {currentLesson.estimated_minutes} minutes
                </p>
              )}
            </div>

            {/* Content Tabs */}
            <div className="bg-white border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {['content', 'notes', 'bookmarks'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-3 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                      activeTab === tab
                        ? 'border-nutrition-500 text-nutrition-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-4xl mx-auto px-6 py-8">
                <AnimatePresence mode="wait">
                  {activeTab === 'content' && (
                    <motion.div
                      key="content"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-8"
                    >
                      {/* Video Player */}
                      {currentLesson.video_embed_url && (
                        <VideoPlayer
                          ref={playerRef}
                          url={currentLesson.video_embed_url}
                          onAddBookmark={handleAddBookmark}
                          bookmarks={bookmarks.filter(b => b.lesson_id === currentLesson.id)}
                        />
                      )}

                      {/* Lesson Content */}
                      <LessonContent lesson={currentLesson} />

                      {/* Quiz */}
                      {currentLessonQuiz && (
                        <LessonQuiz
                          quiz={currentLessonQuiz}
                          attempts={quizAttempts}
                          onSubmit={submitQuizAttempt}
                          onComplete={() => {
                            trackEvent('quiz_completed', {
                              courseId: course.id,
                              moduleId: currentModule.id,
                              lessonId: currentLesson.id,
                              quizId: currentLessonQuiz.id
                            })
                          }}
                        />
                      )}
                    </motion.div>
                  )}

                  {activeTab === 'notes' && (
                    <motion.div
                      key="notes"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <LessonNotes
                        lessonId={currentLesson.id}
                        notes={notes.filter(n => n.lesson_id === currentLesson.id)}
                        onSave={saveNote}
                        loading={notesLoading}
                      />
                    </motion.div>
                  )}

                  {activeTab === 'bookmarks' && (
                    <motion.div
                      key="bookmarks"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <LessonBookmarks
                        lessonId={currentLesson.id}
                        bookmarks={bookmarks.filter(b => b.lesson_id === currentLesson.id)}
                        onAdd={handleAddBookmark}
                        onDelete={deleteBookmark}
                        onSeek={(timestamp) => {
                          if (playerRef.current) {
                            playerRef.current.seekTo(timestamp)
                          }
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Bottom Navigation */}
            <CourseNavigation
              currentLesson={currentLesson}
              allLessons={getAllLessons()}
              isLessonComplete={isLessonComplete(currentLesson.id)}
              canMarkComplete={canMarkComplete}
              completingLesson={completingLesson}
              onPrevious={handlePreviousLesson}
              onNext={handleNextLesson}
              onMarkComplete={handleMarkComplete}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CoursePlayer